import React, { FC, useEffect, useRef, useCallback, KeyboardEventHandler, RefObject } from 'react';

import { CloseIcon } from '../SvgIcons';
import { useScrollLock } from '../../hooks/useAddFeatureFlagToRoot/useScrollLock';
import useFocusTrap from '../../hooks/useFocusTrap';
import useClickOutside from '../../hooks/useClickOutside';
import ModalContainer from './ModalContainer';
import { Overlay, ModalDialog, CloseModalButton, ScreenReaderText } from './Modal.styles';

export type ModalProps = {
  /** A function to perform when the user closes the modal.  Should remove the modal from the DOM. */
  onClose: () => void;
  /** Where the modal will be mounted.  Should be at the root of the app so that the overlay covers the entire window.  Must be separately defined. */
  rootId: string;
  /** The id for the h1 (preferred) or h2 element that will be the title for the modal. If you cannot add a heading and make it visible, you may make the heading visible only to screen reader users. Accessibility Requirement */
  dialogHeadingId: string;
  /** The modal should open based on some user action, preferablly from a button element.  Focus will return to the element upon closing the modal.   Accessibility requirement. */
  triggerRef: RefObject<HTMLButtonElement | HTMLAnchorElement> | null;
  className?: string;
  lockBgScrolling?: boolean;
};

const Modal: FC<ModalProps> = ({
  children,
  dialogHeadingId = 'dialog-heading',
  onClose,
  rootId,
  triggerRef,
  className,
  lockBgScrolling = false,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const handleTabbing = useFocusTrap(dialogRef);
  useScrollLock(lockBgScrolling);

  const onCloseCallback = useCallback(onClose, [onClose]);

  useClickOutside({ ref: dialogRef, onClickOutside: onCloseCallback, isOpen: true });

  useEffect(() => {
    // Close modal when hitting "Esc".
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onCloseCallback();
      }
    };
    window.addEventListener('keydown', close, true);
    const triggeringButton = triggerRef?.current;
    return () => {
      window.removeEventListener('keydown', close, true);
      if (triggeringButton) {
        triggeringButton.focus();
      }
    };
  }, [onCloseCallback, triggerRef]);

  const handlePressEnter: KeyboardEventHandler<HTMLButtonElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      onCloseCallback();
    }
  };

  return (
    <ModalContainer rootId={rootId}>
      <Overlay className={className}>
        <ModalDialog
          role="dialog"
          aria-modal="true"
          aria-labelledby={dialogHeadingId}
          ref={dialogRef}
          onKeyDown={handleTabbing}
        >
          <CloseModalButton
            id="zCloseModal"
            onClick={onCloseCallback}
            onKeyDown={handlePressEnter}
            ref={closeButtonRef}
          >
            <CloseIcon />
            <ScreenReaderText>Close</ScreenReaderText>
          </CloseModalButton>
          {children}
        </ModalDialog>
      </Overlay>
    </ModalContainer>
  );
};

export default Modal;
